import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { AHclimOpModeEnum, AHcurrOpModeEnum, AHglobStatusEnum, isNotNullOrUndefined, System } from 'ngx-core-services';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { requestSelectedHeatpumpCurrentData, selectedSystem, selectedSystemTz, selectedSystemUid, SystemsState } from '../../store';
import { selectHeatpumpFlowChartData } from '../../store/selectors/heatpump.selectors';

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-550': 550,
})
@Component({
  selector: 'lib-heatpump-flow-chart',
  templateUrl: './heatpump-flow-chart.component.html',
  styleUrls: ['./heatpump-flow-chart.component.scss'],
})
export class HeatpumpFlowChartComponent implements ICustomBlock, IReduxBlock {
  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _labels = {
    title: 'Heatpump flow',
    cooling: "Cooling",
    heating: "Heating",
    hotWater: "HotWater",
    modeTitle: 'Mode',
    envTemp: 'Environment Temp',
  };

  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly requestHeatpumpCurrentData$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([systemId, deviceId, forceRefresh, instanceId]) => {
      this.store.dispatch(requestSelectedHeatpumpCurrentData({ systemId, instanceId, deviceId, forceRefresh }));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly systemTimezone$ = this.store.select(selectedSystemTz).pipe(
    isNotNullOrUndefined(),
  );

  readonly selectedHeatpumpCurrentConfig$ = combineLatest([
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystem).pipe(
      map(system => system?.monitoringType === 'AzzurroHub' ? system.data?.heatPump : undefined),
      isNotNullOrUndefined(),
    ),
    this._refresh$,
  ]).pipe(
    switchMap(([deviceId, heatPumps, _]) => {
      const device = (heatPumps ?? []).find((hp: any) => hp.sn === deviceId);
      return of(device);
    }),
    isNotNullOrUndefined(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );


  readonly selectHeatpumpCurrentData$ = combineLatest([
    this._instanceId$,
    this._deviceId$.pipe(isNotNullOrUndefined()),
  ]).pipe(
    switchMap(([instanceId, deviceId]) =>
      this.store
        .select(selectHeatpumpFlowChartData(instanceId, deviceId))
        .pipe(isNotNullOrUndefined())
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
    isNotNullOrUndefined()
  );

  AHcurrOpModeEnum = AHcurrOpModeEnum;
  AHclimOpModeEnum = AHclimOpModeEnum;
  AHglobStatusEnum = AHglobStatusEnum;

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) { }
}
